import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface HeadToHeadMatch {
  id: string;
  fixture_id: number;
  team_home_id: string;
  team_away_id: string;
  league_id: string | null;
  match_date: string;
  home_score: number | null;
  away_score: number | null;
  venue: string | null;
  created_at: string;
  updated_at: string;
}

export interface HeadToHeadStats {
  total_matches: number;
  home_wins: number;
  away_wins: number;
  draws: number;
  home_goals: number;
  away_goals: number;
  avg_goals_per_match: number;
}

export const useHeadToHead = (
  teamHomeId: string | null,
  teamAwayId: string | null
) => {
  return useQuery({
    queryKey: ['head-to-head', teamHomeId, teamAwayId],
    queryFn: async () => {
      if (!teamHomeId || !teamAwayId) return null;

      // Fetch H2H matches (both directions)
      const { data, error } = await supabase
        .from('head_to_head')
        .select('*')
        .or(`and(team_home_id.eq.${teamHomeId},team_away_id.eq.${teamAwayId}),and(team_home_id.eq.${teamAwayId},team_away_id.eq.${teamHomeId})`)
        .order('match_date', { ascending: false })
        .limit(10);

      if (error) {
        console.error('Error fetching H2H data:', error);
        throw error;
      }

      const matches = data as HeadToHeadMatch[];

      // Calculate statistics
      const stats: HeadToHeadStats = {
        total_matches: matches.length,
        home_wins: 0,
        away_wins: 0,
        draws: 0,
        home_goals: 0,
        away_goals: 0,
        avg_goals_per_match: 0
      };

      matches.forEach((match) => {
        const homeIsOriginalHome = match.team_home_id === teamHomeId;
        const homeScore = match.home_score || 0;
        const awayScore = match.away_score || 0;

        // Count wins/draws from perspective of original home team
        if (homeScore > awayScore) {
          if (homeIsOriginalHome) stats.home_wins++;
          else stats.away_wins++;
        } else if (awayScore > homeScore) {
          if (homeIsOriginalHome) stats.away_wins++;
          else stats.home_wins++;
        } else {
          stats.draws++;
        }

        // Count goals from perspective of original teams
        if (homeIsOriginalHome) {
          stats.home_goals += homeScore;
          stats.away_goals += awayScore;
        } else {
          stats.home_goals += awayScore;
          stats.away_goals += homeScore;
        }
      });

      if (stats.total_matches > 0) {
        stats.avg_goals_per_match = 
          Number(((stats.home_goals + stats.away_goals) / stats.total_matches).toFixed(2));
      }

      return {
        matches,
        stats
      };
    },
    enabled: !!teamHomeId && !!teamAwayId,
    staleTime: 604800000, // 7 days - H2H historical data rarely changes
  });
};

// Helper to format H2H stats for display
export const formatH2HStats = (stats: HeadToHeadStats | undefined) => {
  if (!stats) return null;

  const homeWinPercentage = stats.total_matches > 0
    ? Math.round((stats.home_wins / stats.total_matches) * 100)
    : 0;
  
  const awayWinPercentage = stats.total_matches > 0
    ? Math.round((stats.away_wins / stats.total_matches) * 100)
    : 0;
  
  const drawPercentage = stats.total_matches > 0
    ? Math.round((stats.draws / stats.total_matches) * 100)
    : 0;

  return {
    ...stats,
    homeWinPercentage,
    awayWinPercentage,
    drawPercentage,
    mostCommonResult: 
      stats.home_wins > stats.away_wins && stats.home_wins > stats.draws
        ? 'home'
        : stats.away_wins > stats.home_wins && stats.away_wins > stats.draws
        ? 'away'
        : 'draw'
  };
};
